/**
 * FO-391 (batch-46) - Jaarlijkse tarief-indexatie: gedeelde types + Zod-schemas.
 *
 * Kernregel (spec besluit 3): indexeren is VERSIONEREN, nooit muteren, en nooit
 * zonder preview. De oude regel sluit op peildatum - 1 dag; de nieuwe regel start
 * op de peildatum met het geindexeerde bedrag. Wat al gesnapshot is blijft
 * bevroren.
 *
 * De rekenkern zelf (`berekenIndexatie`) staat in `@streep/utils` (Decimal-exact,
 * afronding alleen op het eindbedrag). Dit bestand levert de DTO's, de request-
 * validatie en de classificatie welke velden/ruleTypes bedrag-dragend zijn.
 */

import { z } from 'zod'
import type { AgreementRuleType } from './agreement-rules'
import { AGREEMENT_RULE_TYPE_LABELS } from './agreement-rules'

// ─── Welke velden/ruleTypes worden geindexeerd? ──────────────────────────────
//
// Alleen BEDRAG-dragende velden (euro's) indexeren. Percentages (ploegentoeslag,
// toeslag-percentage) en flags (inbegrepen) NIET: een toeslagpercentage indexeert
// zichzelf via het grondslagbedrag. Aantallen/drempels (max km, minimum uren,
// drempel-minuten) zijn geen bedragen en blijven ongemoeid.

/** Bedrag-dragende ClientTariff-velden (Decimal euro-bedragen). */
export const INDEXEERBARE_CLIENT_TARIFF_VELDEN = [
  'hourlyRate',
  'overtimeRate',
  'travelRatePerKm',
  'reisUurtarief',
  'packageSurchargeAmount',
] as const
export type IndexeerbaarClientTariffVeld = (typeof INDEXEERBARE_CLIENT_TARIFF_VELDEN)[number]

export const CLIENT_TARIFF_VELD_LABELS: Record<IndexeerbaarClientTariffVeld, string> = {
  hourlyRate: 'Uurtarief',
  overtimeRate: 'Overuurtarief',
  travelRatePerKm: 'Reiskosten per km',
  reisUurtarief: 'Reisuren-tarief',
  packageSurchargeAmount: 'Pakkettoeslag (per uur)',
}

/** Bedrag-dragende `ClientAgreementRule.ruleType`s (value = euro). */
export const INDEXEERBARE_RULE_TYPES: readonly AgreementRuleType[] = [
  'VAST_TARIEF_PER_EENHEID',
  'AFWIJKEND_TARIEF',
  'TOESLAG_BEDRAG',
  'DAGVERGOEDING',
  'WEEKVERGOEDING',
  'STANDBY_WEEKTARIEF',
  'STANDBY_DAGTARIEF',
  'OPROEP_UURTARIEF',
]

export function isIndexeerbaarRuleType(ruleType: AgreementRuleType): boolean {
  return INDEXEERBARE_RULE_TYPES.includes(ruleType)
}

export function agreementRuleVeldLabel(ruleType: AgreementRuleType): string {
  return AGREEMENT_RULE_TYPE_LABELS[ruleType] ?? ruleType
}

// ─── Conflict-categorieen ────────────────────────────────────────────────────

/** Rij-niveau conflict: de regel wordt overgeslagen (niet-blokkerend). */
export const INDEXATIE_REGEL_CONFLICTEN = [
  // validUntil/geldigTot valt vOOr de peildatum -> loopt af, niets te indexeren.
  'REGEL_VERVALT',
  // validFrom/geldigVanaf valt NA de peildatum -> al vooruit geprijsd, overslaan.
  'REGEL_VOORUIT_GEPRIJSD',
] as const
export type IndexatieRegelConflict = (typeof INDEXATIE_REGEL_CONFLICTEN)[number]

/** Klant-niveau conflict. `EERDERE_RUN_ZELFDE_PEILDATUM` blokkeert de klant. */
export const INDEXATIE_KLANT_CONFLICTEN = [
  // In default-scope maar geen contract-pct -> alleen via handmatige override.
  'GEEN_CONTRACT_PCT',
  // Eerdere TOEGEPASTE run met dezelfde peildatum -> dubbel-indexatie-guard.
  'EERDERE_RUN_ZELFDE_PEILDATUM',
  // Contract verlopen vOOr de peildatum -> uit default-scope, handmatig met markering.
  'CONTRACT_VERLOPEN',
  // Meerdere actieve contracten -> hoogste pct gekozen, markering (A1).
  'MEERDERE_ACTIEVE_CONTRACTEN',
] as const
export type IndexatieKlantConflict = (typeof INDEXATIE_KLANT_CONFLICTEN)[number]

export const INDEXATIE_KLANT_CONFLICT_LABELS: Record<IndexatieKlantConflict, string> = {
  GEEN_CONTRACT_PCT: 'Geen contract-indexeringspercentage - alleen via handmatige override',
  EERDERE_RUN_ZELFDE_PEILDATUM: 'Al geindexeerd op deze peildatum - klant wordt overgeslagen',
  CONTRACT_VERLOPEN: 'Contract verlopen voor de peildatum - handmatig toegevoegd',
  MEERDERE_ACTIEVE_CONTRACTEN: 'Meerdere actieve contracten - hoogste percentage gekozen',
}

export const INDEXATIE_REGEL_CONFLICT_LABELS: Record<IndexatieRegelConflict, string> = {
  REGEL_VERVALT: 'Loopt af voor de peildatum - niets te indexeren',
  REGEL_VOORUIT_GEPRIJSD: 'Al vooruit geprijsd - overgeslagen',
}

// ─── Model-type ──────────────────────────────────────────────────────────────

export const INDEXATIE_MODEL_TYPES = ['CLIENT_TARIFF', 'AGREEMENT_RULE'] as const
export type IndexatieModelType = (typeof INDEXATIE_MODEL_TYPES)[number]

// ─── DTO's (server -> client) ────────────────────────────────────────────────

export interface IndexatiePreviewRegelDTO {
  model: IndexatieModelType
  /** Id van de bron-regel (ClientTariff.id of ClientAgreementRule.id). */
  bronId: string
  /** Veld (ClientTariff-veld) of ruleType (agreement-rule). */
  veld: string
  veldLabel: string
  /** Bedragen als decimaal-string met 2 decimalen. */
  oud: string
  nieuw: string
  /** Toegepast percentage (decimaal-string). */
  pct: string
  /** Niet-blokkerend rij-conflict; null = normaal indexeerbaar. */
  conflict: IndexatieRegelConflict | null
  /** false = de regel wordt bij toepassen overgeslagen (conflict of geblokkeerde klant). */
  wordtToegepast: boolean
}

export type IndexatiePctBron = 'CONTRACT' | 'OVERRIDE' | 'GEEN'

export interface IndexatiePreviewKlantDTO {
  clientId: string
  clientNaam: string
  /** Toegepast percentage (contract of override), decimaal-string. */
  pct: string
  pctBron: IndexatiePctBron
  /** Percentage uit het (hoogste) actieve contract, indien aanwezig. */
  contractPct: string | null
  regels: IndexatiePreviewRegelDTO[]
  conflicten: IndexatieKlantConflict[]
  /** true = klant wordt bij toepassen volledig overgeslagen. */
  geblokkeerd: boolean
  /**
   * Informatieve voetnoot (v1-besluit): draait deze klant (deels) op globale
   * `Tariff`-defaults? Die worden bewust NIET geindexeerd in v1.
   */
  draaitOpGlobaleDefaults: boolean
}

export interface IndexatiePreviewDTO {
  peildatum: string
  /** Versietoken over de preview-inhoud; vereist bij toepassen (drift -> 409). */
  previewToken: string
  klanten: IndexatiePreviewKlantDTO[]
  aantalKlanten: number
  /** Aantal daadwerkelijk toe te passen regels (excl. conflicten/geblokkeerd). */
  aantalRegels: number
  aantalGeblokkeerd: number
  /** true = er zit een pct buiten (0, 15] in de scope -> tweede bevestiging vereist. */
  vereistHogePctBevestiging: boolean
  /** Informatieve voetnoot over globale Tariff-defaults (v1-besluit), of null. */
  globaleTariefVoetnoot: string | null
}

// ─── Run-DTO's ───────────────────────────────────────────────────────────────

export type IndexatieRunStatus = 'CONCEPT' | 'TOEGEPAST' | 'TERUGGEDRAAID'

export const INDEXATIE_RUN_STATUS_LABELS: Record<IndexatieRunStatus, string> = {
  CONCEPT: 'Concept',
  TOEGEPAST: 'Toegepast',
  TERUGGEDRAAID: 'Teruggedraaid',
}

export interface IndexatieRunRegelDTO {
  id: string
  clientId: string
  clientNaam: string | null
  model: IndexatieModelType
  veld: string
  veldLabel: string
  pct: string
  oudBedrag: string
  nieuwBedrag: string
  oudeRegelId: string | null
  nieuweRegelId: string | null
  teruggedraaidOp: string | null
}

export interface IndexatieRunKlantDTO {
  clientId: string
  clientNaam: string | null
  aantalRegels: number
  /** true = na de peildatum bestaat een tarief-snapshot -> terugdraaien geeft 409. */
  heeftSnapshotNaPeildatum: boolean
}

export interface IndexatieRunDTO {
  id: string
  peildatum: string
  status: IndexatieRunStatus
  uitgevoerdDoor: string | null
  uitgevoerdDoorNaam: string | null
  uitgevoerdOp: string
  teruggedraaidDoor: string | null
  teruggedraaidDoorNaam: string | null
  teruggedraaidOp: string | null
  terugdraaiReden: string | null
  aantalKlanten: number
  aantalRegels: number
  regels?: IndexatieRunRegelDTO[]
  klanten?: IndexatieRunKlantDTO[]
}

export interface IndexatieTerugdraaiResultaatDTO {
  runId: string
  status: IndexatieRunStatus
  teruggedraaideKlanten: number
  teruggedraaideRegels: number
  /** Klanten die NIET teruggedraaid konden worden (snapshot na peildatum). */
  geweigerd: Array<{
    clientId: string
    clientNaam: string | null
    reden: string
  }>
}

// ─── Zod-schemas (request-validatie, gedeeld client + server) ────────────────

/** yyyy-mm-dd (NL-kalenderdag). */
const peildatumSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Peildatum moet het formaat jjjj-mm-dd hebben')

/** Percentage als decimaal-string, 0 < pct, max 2 decimalen. */
const pctStringSchema = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,2})?$/, 'Percentage moet een getal zijn (max 2 decimalen)')
  .refine((v) => Number(v) > 0, 'Percentage moet groter dan 0 zijn')

export const IndexatieScopeModus = ['DEFAULT', 'SELECTIE'] as const
export type IndexatieScopeModusType = (typeof IndexatieScopeModus)[number]

export const IndexatiePreviewRequestSchema = z.object({
  peildatum: peildatumSchema,
  /**
   * DEFAULT = alle ACTIEVE contracten met indexeringPct.
   * SELECTIE = uitsluitend `clientIds`.
   */
  modus: z.enum(IndexatieScopeModus).default('DEFAULT'),
  /** Handmatige klant-selectie (verplicht bij SELECTIE; extra klanten bij DEFAULT). */
  clientIds: z.array(z.string().min(1)).max(2000).optional(),
  /** Per-klant percentage-override. Sleutel = clientId, waarde = decimaal-string. */
  pctOverrides: z.record(z.string().min(1), pctStringSchema).optional(),
})
export type IndexatiePreviewRequest = z.infer<typeof IndexatiePreviewRequestSchema>

export const IndexatieToepassenRequestSchema = IndexatiePreviewRequestSchema.extend({
  /** Versietoken uit de preview; drift tussen preview en toepassen -> 409. */
  previewToken: z.string().min(1),
  /** Tweede bevestiging: vereist zodra een pct buiten (0, 15] in de scope zit. */
  bevestigHogePercentages: z.boolean().optional(),
})
export type IndexatieToepassenRequest = z.infer<typeof IndexatieToepassenRequestSchema>

export const IndexatieTerugdraaiRequestSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een reden op (min. 3 tekens)').max(500),
})
export type IndexatieTerugdraaiRequest = z.infer<typeof IndexatieTerugdraaiRequestSchema>
